import { uiSurface } from "shared/ui/uiSurface"
import { Add, Delete } from "@mui/icons-material"
import {
  Box,
  IconButton,
  Link,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import type { AddressEntry } from "./useOrderCreateForm"

type Props = {
  addresses: AddressEntry[]
  selectedAddressIdx: number
  onSetAddress: (index: number, field: keyof AddressEntry, value: string) => void
  onAddAddress: () => void
  onRemoveAddress: (index: number) => void
  onSelectAddress: (index: number) => void
}

export const AddressList: FC<Props> = ({
  addresses,
  selectedAddressIdx,
  onSetAddress,
  onAddAddress,
  onRemoveAddress,
  onSelectAddress,
}) => {
  const { t } = useTranslation()

  return (
    <Box {...uiSurface("UI-DLV2-038")}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
        {t("orderCreate.addresses")}
      </Typography>
      <Stack spacing={1.5}>
        {addresses.map((addr, idx) => (
          <Box
            key={idx}
            sx={{
              p: 1.5,
              border: 1,
              borderColor: selectedAddressIdx === idx ? "primary.main" : "divider",
              borderRadius: 1,
              bgcolor: "transparent",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Radio
                  size="small"
                  checked={selectedAddressIdx === idx}
                  onChange={() => onSelectAddress(idx)}
                  data-testid={`order-create-address-radio-${idx}`}
                />
                <Typography variant="caption" fontWeight={500}>
                  {addr.label || t("orderCreate.address")} {idx + 1}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => onRemoveAddress(idx)}
                data-testid={`order-create-address-remove-${idx}`}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label={t("orderCreate.streetNumber")}
                  value={addr.streetNumber}
                  onChange={(e) => onSetAddress(idx, "streetNumber", e.target.value)}
                  sx={{ width: 120 }}
                  data-testid={`order-create-address-streetNumber-${idx}`}
                />
                <TextField
                  size="small"
                  label={t("orderCreate.streetName")}
                  value={addr.streetName}
                  onChange={(e) => onSetAddress(idx, "streetName", e.target.value)}
                  fullWidth
                  data-testid={`order-create-address-streetName-${idx}`}
                />
                <TextField
                  size="small"
                  label={t("orderCreate.apt")}
                  value={addr.appartNumber}
                  onChange={(e) => onSetAddress(idx, "appartNumber", e.target.value)}
                  sx={{ width: 80 }}
                  data-testid={`order-create-address-apt-${idx}`}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label={t("orderCreate.city")}
                  value={addr.city}
                  onChange={(e) => onSetAddress(idx, "city", e.target.value)}
                  fullWidth
                  data-testid={`order-create-address-city-${idx}`}
                />
                <TextField
                  size="small"
                  label={t("orderCreate.province")}
                  value={addr.state}
                  onChange={(e) => onSetAddress(idx, "state", e.target.value)}
                  sx={{ width: 140 }}
                  data-testid={`order-create-address-state-${idx}`}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label={t("orderCreate.postcode")}
                  value={addr.postcode}
                  onChange={(e) => onSetAddress(idx, "postcode", e.target.value)}
                  sx={{ width: 140 }}
                  data-testid={`order-create-address-postcode-${idx}`}
                />
                <TextField
                  size="small"
                  label={t("orderCreate.country")}
                  value={addr.country}
                  onChange={(e) => onSetAddress(idx, "country", e.target.value)}
                  fullWidth
                  data-testid={`order-create-address-country-${idx}`}
                />
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
      <Link
        component="button"
        variant="caption"
        onClick={onAddAddress}
        sx={{ mt: 0.5, display: "inline-flex", alignItems: "center", gap: 0.5 }}
        data-testid="order-create-address-add"
      >
        <Add sx={{ fontSize: 14 }} /> {t("orderCreate.addAddress")}
      </Link>
    </Box>
  )
}
